import { FundWithDetailsEntity, ProtocolEntity, SiteSettingsEntity } from '@/src/lib/MayzSmartDB/Entities/index';
import { Action, Computed, Thunk, action, computed, thunk } from 'easy-peasy';
import { FundWithDetailsApi, ProtocolApi, WalletApi } from '../../lib/MayzSmartDB';
import { isEmulator } from '@/src/lib/SmartDB/Commons/Constants/constants';
import { IWalletStoreModel } from './walletStoreModel';

//------------------------------------

export interface IAppStoreModel {
    swInitApiCompleted?: boolean;
    setSwInitApiCompleted: Action<IAppStoreModel, boolean | undefined>;

    isNavigating: boolean;
    setIsNavigating: Action<IAppStoreModel, boolean>;

    navigationTimeoutId: NodeJS.Timeout | null;
    setNavigationTimeoutId: Action<IAppStoreModel, NodeJS.Timeout | null>;

    swExistProtocol?: boolean;
    setSwExistProtocol: Action<IAppStoreModel, boolean | undefined>;
    checkIfExistProtocol: Thunk<IAppStoreModel, undefined, any, {}, Promise<boolean>>;

    swExistAnyWallet?: boolean;
    setSwExistAnyWallet: Action<IAppStoreModel, boolean | undefined>;
    checkIfExistAnyWallet: Thunk<IAppStoreModel, undefined, any, {}, Promise<boolean>>;

    siteSettings?: SiteSettingsEntity;
    setSiteSettings: Action<IAppStoreModel, SiteSettingsEntity | undefined>;

    protocol?: ProtocolEntity;
    setProtocol: Action<IAppStoreModel, ProtocolEntity | undefined>;

    isProtocolLoading: boolean;
    setIsProtocolLoading: Action<IAppStoreModel, boolean>;
    isProtocolLoaded: boolean;
    setIsProtocolLoaded: Action<IAppStoreModel, boolean>;

    loadProtocol: Thunk<IAppStoreModel>;

    funds?: FundWithDetailsEntity[];
    setFunds: Action<IAppStoreModel, FundWithDetailsEntity[] | undefined>;
    isFundsLoading: boolean;
    setIsFundsLoading: Action<IAppStoreModel, boolean>;
    isFundsLoaded: boolean;
    setIsFundsLoaded: Action<IAppStoreModel, boolean>;
    loadFunds: Thunk<IAppStoreModel, undefined, any, FundWithDetailsEntity[], any>;

    isAppStoreLoading: Computed<IAppStoreModel & IWalletStoreModel, boolean>;
    isAppStoreLoaded: Computed<IAppStoreModel & IWalletStoreModel, boolean>;

    isProcessingTx: boolean;
    setIsProcessingTx: Action<IAppStoreModel, boolean>;
}

export const AppStoreModel: IAppStoreModel = {
    swInitApiCompleted: undefined,
    setSwInitApiCompleted: action((state, swInitApiCompleted) => {
        state.swInitApiCompleted = swInitApiCompleted;
    }),

    isNavigating: false,
    setIsNavigating: action((state, isNavigating) => {
        state.isNavigating = isNavigating;
    }),

    navigationTimeoutId: null,
    setNavigationTimeoutId: action((state, navigationTimeoutId) => {
        state.navigationTimeoutId = navigationTimeoutId;
    }),

    swExistProtocol: undefined,
    setSwExistProtocol: action((state, swExistProtocol) => {
        state.swExistProtocol = swExistProtocol;
    }),
    checkIfExistProtocol: thunk(async (actions, _, helpers) => {
        const state = helpers.getState();
        console.log(`[AppStore] - checkIfExistProtocol - Init`);
        try {
            actions.setSwExistProtocol(undefined);
            //--------------------------------------
            const { count } = await ProtocolApi.getCountApi_();
            //--------------------------------------
            const result = count > 0;
            //--------------------------------------
            actions.setSwExistProtocol(result);
            //--------------------------------------
            console.log(`[AppStore] - checkIfExistProtocol - OK`);
            //--------------------------------------
            return result;
            //--------------------------------------
        } catch (error) {
            console.log(`[AppStore] - checkIfExistProtocol - Error: ${error}`);
            throw error;
        }
    }),

    swExistAnyWallet: undefined,
    setSwExistAnyWallet: action((state, swExistAnyWallet) => {
        state.swExistAnyWallet = swExistAnyWallet;
    }),
    checkIfExistAnyWallet: thunk(async (actions, _, helpers) => {
        const state = helpers.getState();
        console.log(`[AppStore] - checkIfExistAnyWallet - Init`);
        try {
            actions.setSwExistAnyWallet(undefined);
            //--------------------------------------
            const { count } = await WalletApi.getCountApi_();
            //--------------------------------------
            const result = count > 0;
            //--------------------------------------
            actions.setSwExistAnyWallet(result);
            //--------------------------------------
            console.log(`[AppStore] - checkIfExistAnyWallet - OK`);
            //--------------------------------------
            return result;
            //--------------------------------------
        } catch (error) {
            console.log(`[AppStore] - checkIfExistAnyWallet - Error: ${error}`);
            throw error;
        }
    }),

    siteSettings: undefined,
    setSiteSettings: action((state, payload) => {
        state.siteSettings = new SiteSettingsEntity(payload);
    }),

    protocol: undefined,
    setProtocol: action((state, payload) => {
        state.protocol = payload;
    }),

    isProtocolLoading: false,
    setIsProtocolLoading: action((state, payload) => {
        state.isProtocolLoading = payload;
    }),

    isProtocolLoaded: false,
    setIsProtocolLoaded: action((state, payload) => {
        state.isProtocolLoaded = payload;
    }),

    loadProtocol: thunk(async (actions, _, helpers) => {
        //TODO: no estoy usando para cargar fondos... no tiene mucho sentido eso, otros usaurios pueden estar creando fondos y este usuario en su store no va a ver esos cambios reflejados
        const state = helpers.getState();
        console.log(`[AppStore] - loadProtocol Init`);
        try {
            actions.setIsProtocolLoaded(false);
            actions.setIsProtocolLoading(true);
            //--------------------------------------
            const loadProtocolList = async () => {
                const params = {};
                const protocols: ProtocolEntity[] = await ProtocolApi.getByParamsApi_(params, ProtocolEntity.optionsGetForAppStoreProtocolLists);
                return protocols;
            };
            //--------------------------------------
            const protocols = await loadProtocolList();
            //--------------------------------------
            if (protocols.length === 0) {
                throw new Error('No protocol found');
            }
            //--------------------------------------
            const protocol = protocols[0];
            actions.setProtocol(protocol);
            //--------------------------------------
            actions.setIsProtocolLoaded(true);
            actions.setIsProtocolLoading(false);
            //--------------------------------------
            console.log(`[AppStore] - loadProtocol - OK`);
        } catch (error) {
            console.log(`[AppStore] - loadProtocol - Error: ${error}`);
            actions.setIsProtocolLoading(false);
            throw error;
        }
    }),

    funds: undefined,
    setFunds: action((state, payload) => {
        state.funds = payload;
    }),
    isFundsLoaded: false,
    setIsFundsLoaded: action((state, payload) => {
        state.isFundsLoaded = payload;
    }),
    isFundsLoading: false,
    setIsFundsLoading: action((state, payload) => {
        state.isFundsLoading = payload;
    }),
    loadFunds: thunk(async (actions, _, helpers) => {
        const state = helpers.getState();
        console.log(`[AppStore] - loadFunds Init`);
        try {
            actions.setIsFundsLoaded(false);
            actions.setIsFundsLoading(true);
            //--------------------------------------
            const loadFundsList = async () => {
                const funds: FundWithDetailsEntity[] = await FundWithDetailsApi.getByParamsApi_(
                    { _isDeployed: true },
                    { ...FundWithDetailsEntity.optionsGetForFundList_Only_Name_And_BasicTokens_SortedByTVL, doCallbackAfterLoad: false }
                );
                return funds;
            };
            //--------------------------------------
            const funds = await loadFundsList();
            //--------------------------------------
            actions.setFunds(funds);
            //--------------------------------------
            actions.setIsFundsLoaded(true);
            actions.setIsFundsLoading(false);
            //--------------------------------------
            console.log(`[AppStore] - loadFunds - OK`);
            //--------------------------------------
            return funds;
        } catch (error) {
            console.log(`[AppStore] - loadFunds - Error: ${error}`);
            actions.setIsFundsLoading(false);
            throw error;
        }
    }),

    isAppStoreLoading: computed((state) => {
        return (
            state.swInitApiCompleted === false ||
            state.isProtocolLoading === true ||
            state.siteSettings === undefined ||
            state.swExistProtocol === undefined ||
            state.swExistAnyWallet === undefined ||
            (state.swExistProtocol === true && state.protocol === undefined) ||
            (isEmulator === true && state.emulatorDB === undefined)
        );
    }),

    isAppStoreLoaded: computed((state) => {
        return (
            state.swInitApiCompleted === true ||
            (state.isProtocolLoading === false &&
                state.siteSettings !== undefined &&
                state.swExistProtocol !== undefined &&
                state.swExistAnyWallet !== undefined &&
                (state.swExistProtocol === false || state.protocol !== undefined) &&
                (isEmulator === false || state.emulatorDB !== undefined))
        );
    }),

    isProcessingTx: false,
    setIsProcessingTx: action((state, payload) => {
        state.isProcessingTx = payload;
    }),
};

//------------------------------------
